import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, Loader2, Paperclip, X, CheckCircle2, Bold, Italic, Underline, List, ListOrdered, Link, Clock, Trash2, Calendar, Repeat, Upload, Download, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Toolbar() {
  const exec = (cmd, val) => document.execCommand(cmd, false, val);
  const btnClass = "h-8 w-8 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded";
  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50 rounded-t-lg flex-wrap">
      <button type="button" className={btnClass} onClick={() => exec("bold")} title="Bold"><Bold className="h-4 w-4 mx-auto" /></button>
      <button type="button" className={btnClass} onClick={() => exec("italic")} title="Italic"><Italic className="h-4 w-4 mx-auto" /></button>
      <button type="button" className={btnClass} onClick={() => exec("underline")} title="Underline"><Underline className="h-4 w-4 mx-auto" /></button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <button type="button" className={btnClass} onClick={() => exec("insertUnorderedList")} title="Bullet list"><List className="h-4 w-4 mx-auto" /></button>
      <button type="button" className={btnClass} onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered className="h-4 w-4 mx-auto" /></button>
      <div className="w-px h-5 bg-slate-200 mx-1" />
      <select className="h-8 text-xs border border-slate-200 rounded px-1 bg-white text-slate-700" onChange={(e) => { if (e.target.value) exec("formatBlock", e.target.value); }} defaultValue="">
        <option value="" disabled>Heading</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="p">Normal</option>
      </select>
      <button type="button" className={btnClass} onClick={() => { const url = prompt("Enter link URL:"); if (url) exec("createLink", url); }} title="Insert link"><Link className="h-4 w-4 mx-auto" /></button>
      <select className="h-8 text-xs border border-slate-200 rounded px-1 bg-white text-slate-700" onChange={(e) => { if (e.target.value) exec("foreColor", e.target.value); }} defaultValue="">
        <option value="" disabled>Color</option>
        <option value="#000000">Black</option>
        <option value="#dc2626">Red</option>
        <option value="#2563eb">Blue</option>
        <option value="#16a34a">Green</option>
        <option value="#d97706">Orange</option>
      </select>
    </div>
  );
}

const AUDIENCE_LABELS = { all: "All Users", zkteco: "ZKTeco", distributor: "Distributors", installer: "Installers", end_user: "End Users", marketing_list: "Marketing List", non_members: "Non-Members Only", admin_only: "Just Me (Test)" };
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function BroadcastEmail({ onBack }) {
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const editorRef = useRef(null);
  const fileRef = useRef(null);

  // Schedule state
  const [sendMode, setSendMode] = useState("now"); // "now", "once", "recurring"
  const [sendAt, setSendAt] = useState("");
  const [recurrence, setRecurrence] = useState("weekly");
  const [recurrenceDay, setRecurrenceDay] = useState("0");
  const [recurrenceTime, setRecurrenceTime] = useState("09:00");

  // Audience state
  const [audience, setAudience] = useState("all"); // "all", "zkteco", "distributor", "installer", "end_user"

  // Scheduled list
  const [scheduled, setScheduled] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);

  // Marketing list
  const [marketingCount, setMarketingCount] = useState(0);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvMessage, setCsvMessage] = useState("");
  const csvRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchScheduled();
    fetchMarketingCount();
  }, []);

  const fetchScheduled = async () => {
    try {
      const res = await axios.get(`${API}/broadcast/scheduled`, { headers: getAuthHeaders() });
      setScheduled(res.data || []);
    } catch (err) {
      console.error("Failed to fetch scheduled:", err);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const fetchMarketingCount = async () => {
    try {
      const res = await axios.get(`${API}/marketing/contacts/count`, { headers: getAuthHeaders() });
      setMarketingCount(res.data.count || 0);
    } catch (err) {
      console.error("Failed to fetch marketing count:", err);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/marketing/contacts/download`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "marketing_contacts.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV download failed:", err);
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCsv(true);
    setCsvMessage("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/marketing/contacts/upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setCsvMessage(`Uploaded ${res.data.count} contacts.`);
      setMarketingCount(res.data.count);
    } catch (err) {
      console.error("CSV upload failed:", err);
      setCsvMessage("Upload failed. Check CSV format.");
    } finally {
      setUploadingCsv(false);
      e.target.value = "";
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { setError(`File "${file.name}" exceeds 10MB limit.`); continue; }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setAttachments((prev) => [...prev, { filename: file.name, content: base64, size: file.size }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const handleSend = async () => {
    if (!subject.trim()) { setError("Subject is required."); return; }
    const html = editorRef.current?.innerHTML || "";
    if (!html.trim() || html === "<br>") { setError("Email body is required."); return; }
    setError("");
    setSending(true);

    try {
      if (sendMode === "now") {
        const res = await axios.post(`${API}/broadcast/send`, {
          subject: subject.trim(), html_content: html, audience,
          attachments: attachments.map((a) => ({ filename: a.filename, content: a.content })),
        }, { headers: getAuthHeaders() });
        setResult(res.data);
        setSent(true);
      } else if (sendMode === "once") {
        if (!sendAt) { setError("Please select a date and time."); setSending(false); return; }
        const res = await axios.post(`${API}/broadcast/schedule`, {
          subject: subject.trim(), html_content: html, audience,
          attachments: attachments.map((a) => ({ filename: a.filename, content: a.content })),
          schedule_type: "once", send_at: new Date(sendAt).toISOString(),
        }, { headers: getAuthHeaders() });
        setResult({ scheduled: true, send_at: sendAt });
        setSent(true);
      } else if (sendMode === "recurring") {
        const res = await axios.post(`${API}/broadcast/schedule`, {
          subject: subject.trim(), html_content: html, audience,
          attachments: attachments.map((a) => ({ filename: a.filename, content: a.content })),
          schedule_type: "recurring", recurrence, recurrence_day: parseInt(recurrenceDay), recurrence_time: recurrenceTime,
        }, { headers: getAuthHeaders() });
        setResult({ scheduled: true, recurrence, recurrence_day: recurrenceDay, recurrence_time: recurrenceTime });
        setSent(true);
      }
    } catch (err) {
      console.error("Broadcast failed:", err);
      setError(err.response?.data?.detail || "Failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this scheduled broadcast?")) return;
    try {
      await axios.delete(`${API}/broadcast/scheduled/${id}`, { headers: getAuthHeaders() });
      setScheduled((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Failed to cancel:", err);
    }
  };

  if (sent && result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md text-center" data-testid="broadcast-success">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          {result.scheduled ? (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Broadcast Scheduled!</h2>
              <p className="text-slate-600">
                {result.send_at
                  ? `Scheduled for ${new Date(result.send_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
                  : `Recurring ${result.recurrence} at ${result.recurrence_time}`}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Broadcast Sent!</h2>
              <p className="text-slate-600">Email sent to <strong>{result.total_recipients}</strong> user{result.total_recipients !== 1 ? "s" : ""}.</p>
              {result.failed > 0 && <p className="text-sm text-orange-600 mt-2">{result.failed} failed to deliver.</p>}
            </>
          )}
          <Button onClick={onBack} className="mt-6">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="broadcast-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="broadcast-title">Broadcast Email</h1>
          <p className="text-sm text-slate-500 mt-1">Send an email to all registered users of the configurator.</p>
        </div>

        {/* Scheduled Broadcasts */}
        {scheduled.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 mb-6" data-testid="scheduled-list">
            <h2 className="text-base font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" /> Scheduled Broadcasts
            </h2>
            <div className="space-y-2">
              {scheduled.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3" data-testid={`scheduled-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{s.subject}</p>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                      <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-semibold">{AUDIENCE_LABELS[s.audience] || "All Users"}</span>
                      {s.schedule_type === "once" ? (
                        <>
                          <Calendar className="h-3 w-3" />
                          {new Date(s.send_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                        </>
                      ) : (
                        <>
                          <Repeat className="h-3 w-3" />
                          {s.recurrence === "weekly" ? `Every ${DAYS_OF_WEEK[s.recurrence_day]}` : `Monthly on day ${s.recurrence_day}`} at {s.recurrence_time}
                        </>
                      )}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200 ml-3" onClick={() => handleCancel(s.id)} data-testid={`cancel-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compose */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="mb-4">
            <Label className="text-sm font-semibold text-slate-700">Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line..." className="mt-1" data-testid="broadcast-subject" />
          </div>

          <div className="mb-4">
            <Label className="text-sm font-semibold text-slate-700">Email Body *</Label>
            <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
              <Toolbar />
              <div ref={editorRef} contentEditable className="min-h-[250px] p-4 text-sm text-slate-800 focus:outline-none" style={{ lineHeight: 1.6 }} data-testid="broadcast-editor" suppressContentEditableWarning />
            </div>
          </div>

          <div className="mb-5">
            <Label className="text-sm font-semibold text-slate-700">Attachments</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {attachments.map((att, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 text-sm">
                  <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-slate-700">{att.filename}</span>
                  <span className="text-slate-400 text-xs">({formatSize(att.size)})</span>
                  <button onClick={() => removeAttachment(idx)} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="text-sm" data-testid="broadcast-attach-btn">
                <Paperclip className="h-3.5 w-3.5 mr-1" /> Add File
              </Button>
              <input ref={fileRef} type="file" className="hidden" onChange={handleFileSelect} multiple />
            </div>
            <p className="text-xs text-slate-400 mt-1">Max 10MB per file</p>
          </div>

          {/* Audience */}
          <div className="mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-sm font-semibold text-slate-700 mb-3 block">Audience</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All Users" },
                { value: "zkteco", label: "ZKTeco" },
                { value: "distributor", label: "Distributors" },
                { value: "installer", label: "Installers" },
                { value: "end_user", label: "End Users" },
                { value: "marketing_list", label: `Marketing List (${marketingCount})` },
                { value: "non_members", label: "Non-Members Only" },
                { value: "admin_only", label: "Just Me (Test)" },
              ].map(({ value, label }) => (
                <Button key={value} type="button" variant={audience === value ? "default" : "outline"} size="sm" onClick={() => setAudience(value)} className="text-sm" data-testid={`audience-${value}`}>
                  {label}
                </Button>
              ))}
            </div>

            {/* Marketing List CSV Management */}
            {(audience === "marketing_list" || audience === "non_members") && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Marketing Contacts: {marketingCount}</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">CSV format: First Name, Family Name, Business Name, Email</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleDownloadCsv} className="text-sm" data-testid="download-marketing-csv">
                    <Download className="h-3.5 w-3.5 mr-1" /> Download CSV
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => csvRef.current?.click()} disabled={uploadingCsv} className="text-sm" data-testid="upload-marketing-csv">
                    {uploadingCsv ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                    Upload CSV
                  </Button>
                  <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={handleUploadCsv} />
                </div>
                {csvMessage && <p className="text-xs text-green-600 font-medium mt-2" data-testid="csv-message">{csvMessage}</p>}
              </div>
            )}
          </div>

          {/* Send Mode */}
          <div className="mb-5 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-sm font-semibold text-slate-700 mb-3 block">When to send</Label>
            <div className="flex gap-2 mb-3">
              {[
                { value: "now", label: "Send Now", icon: Send },
                { value: "once", label: "Schedule Once", icon: Calendar },
                { value: "recurring", label: "Recurring", icon: Repeat },
              ].map(({ value, label, icon: Icon }) => (
                <Button key={value} type="button" variant={sendMode === value ? "default" : "outline"} size="sm" onClick={() => setSendMode(value)} className="text-sm" data-testid={`mode-${value}`}>
                  <Icon className="h-3.5 w-3.5 mr-1" /> {label}
                </Button>
              ))}
            </div>

            {sendMode === "once" && (
              <div>
                <Label className="text-xs text-slate-600">Date & Time</Label>
                <Input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} className="mt-1 w-64" data-testid="schedule-datetime" />
              </div>
            )}

            {sendMode === "recurring" && (
              <div className="flex flex-wrap gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Frequency</Label>
                  <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="recurrence-freq">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">{recurrence === "weekly" ? "Day of Week" : "Day of Month"}</Label>
                  {recurrence === "weekly" ? (
                    <select value={recurrenceDay} onChange={(e) => setRecurrenceDay(e.target.value)} className="mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="recurrence-day">
                      {DAYS_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  ) : (
                    <Input type="number" min="1" max="28" value={recurrenceDay} onChange={(e) => setRecurrenceDay(e.target.value)} className="mt-1 w-20 h-9" data-testid="recurrence-day" />
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Time</Label>
                  <Input type="time" value={recurrenceTime} onChange={(e) => setRecurrenceTime(e.target.value)} className="mt-1 w-28 h-9" data-testid="recurrence-time" />
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 mb-4" data-testid="broadcast-error">{error}</p>}

          <Button onClick={handleSend} disabled={sending} className="w-full h-11 text-base font-semibold bg-green-600 hover:bg-green-700" data-testid="broadcast-send-btn">
            {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            {sending ? "Processing..." : sendMode === "now" ? "Send to All Users" : "Schedule Broadcast"}
          </Button>
        </div>
      </div>
    </div>
  );
}
